import { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Upload, Loader2, Check } from "lucide-react";

/**
 * Signed Cloudinary upload widget. Admin-only.
 * Falls back gracefully if Cloudinary keys are not configured.
 */
export default function CloudinaryUpload({ value, onChange, resourceType = "image", label = "Photo" }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const pick = () => inputRef.current?.click();

  const upload = async (file) => {
    setUploading(true); setError(null);
    try {
      const { data: sig } = await api.get(`/cloudinary/signature?folder=dogs&resource_type=${resourceType}`);
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);

      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`, {
        method: "POST",
        body: form,
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error?.message || "Upload failed");
      onChange(json.secure_url);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Upload failed";
      setError(msg.includes("Cloudinary not configured")
        ? "Cloudinary is not configured yet. Add keys in backend/.env or paste an image URL below."
        : msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.15em] mb-2" style={{ color: "#E27E4E" }}>{label}</div>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          className="btn-outline flex-shrink-0"
          style={{ padding: "0.6rem 1rem", fontSize: "0.8rem" }}
          data-testid={`upload-${resourceType}-button`}
        >
          {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</>
            : value ? <><Check size={14} /> Replace {resourceType}</>
            : <><Upload size={14} /> Upload {resourceType}</>}
        </button>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`or paste ${resourceType} URL`}
          className="form-input flex-1"
          data-testid={`${resourceType}-url-input`}
        />
      </div>
      {value && resourceType === "image" && (
        <img src={value} alt="preview" className="mt-3 w-32 h-32 object-cover rounded-xl" />
      )}
      {error && <div className="text-xs mt-2" style={{ color: "#E27E4E" }}>{error}</div>}
      <input
        ref={inputRef}
        type="file"
        accept={resourceType === "image" ? "image/*" : "video/*"}
        className="hidden"
        onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
      />
    </div>
  );
}
